import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const C = {
  bg:      '#F8FDF9',
  dark:    '#0F2D1E',
  primary: '#2D6A4F',
  accent:  '#52B788',
  amber:   '#F59E0B',
  white:   '#FFFFFF',
};

const ff = {
  head: 'Fredoka One, sans-serif',
  body: 'Nunito, sans-serif',
};

const RESPONSIVE_CSS = `
  .mp-nav-desktop { display: flex; align-items: center; gap: 16px; }
  .mp-nav-hamburger { display: none; }
  .mp-hero-inner { flex-direction: row; }
  .mp-hero-phone { display: flex; }
  .mp-how-grid { grid-template-columns: repeat(3, 1fr); }
  .mp-parent-grid { grid-template-columns: repeat(3, 1fr); }
  .mp-kid-grid { grid-template-columns: repeat(3, 1fr); }
  .mp-pricing-grid { grid-template-columns: repeat(2, 1fr); }
  .mp-footer-inner { flex-direction: row; align-items: center; }

  @media (max-width: 768px) {
    .mp-nav-desktop { display: none !important; }
    .mp-nav-hamburger { display: block !important; }
    .mp-hero-inner { flex-direction: column; }
    .mp-hero-phone { display: none !important; }
    .mp-how-grid { grid-template-columns: 1fr !important; }
    .mp-parent-grid { grid-template-columns: 1fr !important; }
    .mp-kid-grid { grid-template-columns: 1fr !important; }
    .mp-pricing-grid { grid-template-columns: 1fr !important; }
    .mp-footer-inner { flex-direction: column; text-align: center; }
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    .mp-parent-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .mp-kid-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }

  .mp-amber-btn {
    display: inline-block;
    background: #F59E0B;
    color: #0F2D1E;
    font-family: 'Nunito', sans-serif;
    font-weight: 800;
    text-decoration: none;
    border-radius: 14px;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .mp-amber-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(245,158,11,0.45);
  }
  .mp-outline-btn {
    display: inline-block;
    background: transparent;
    color: #FFFFFF;
    font-family: 'Nunito', sans-serif;
    font-weight: 800;
    text-decoration: none;
    border-radius: 14px;
    border: 2px solid rgba(255,255,255,0.5);
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  .mp-outline-btn:hover {
    border-color: rgba(255,255,255,0.8);
    background: rgba(255,255,255,0.08);
  }
  .mp-free-btn {
    display: block;
    text-align: center;
    border: 2px solid #2D6A4F;
    color: #2D6A4F;
    font-family: 'Nunito', sans-serif;
    font-weight: 800;
    font-size: 15px;
    padding: 13px 24px;
    border-radius: 14px;
    text-decoration: none;
    transition: background 0.15s;
  }
  .mp-free-btn:hover { background: #f0faf4; }
  .mp-pro-btn {
    display: block;
    text-align: center;
    background: #F59E0B;
    color: #0F2D1E;
    font-family: 'Nunito', sans-serif;
    font-weight: 800;
    font-size: 15px;
    padding: 13px 24px;
    border-radius: 14px;
    text-decoration: none;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .mp-pro-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(245,158,11,0.4); }
  .mp-footer-link { color: rgba(255,255,255,0.6); text-decoration: none; font-family: 'Nunito', sans-serif; font-size: 13px; font-weight: 700; transition: color 0.15s; }
  .mp-footer-link:hover { color: #52B788; }
  .mp-download-btn {
    display: inline-block;
    background: #F59E0B;
    color: #0F2D1E;
    font-family: 'Nunito', sans-serif;
    font-weight: 800;
    font-size: 20px;
    padding: 20px 52px;
    border-radius: 18px;
    text-decoration: none;
    box-shadow: 0 8px 32px rgba(245,158,11,0.4);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .mp-download-btn:hover { transform: translateY(-3px); box-shadow: 0 16px 48px rgba(245,158,11,0.5); }
`;

const faqs = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'What is ThriveTrack?',
        a: 'ThriveTrack is a family habit and chore tracking app built on behavioral science. Parents assign chores and goals, kids earn XP points for completing them, level up through a progression system, and redeem coins for real rewards. It\'s designed to build genuine consistency — not just compliance — using the same mechanics that make games impossible to quit.'
      },
      {
        q: 'How do I set up ThriveTrack for my family?',
        a: 'Download the app, create a parent account, then add each child as a profile. You\'ll assign chores and goals with XP values, and each kid gets their own view showing their missions, current level, and reward balance. Most families are fully set up in under 10 minutes — no tutorial required.'
      },
      {
        q: 'What age range is ThriveTrack designed for?',
        a: 'ThriveTrack works best for kids ages 4–17. Younger children (4–7) benefit from visual tasks and parent-led XP celebration. Older kids (8–17) engage with the leaderboard, streak competition, and coin-to-reward system on their own. The app is designed so it grows with your kid.'
      },
      {
        q: 'Is ThriveTrack available on iPhone and Android?',
        a: 'Yes — ThriveTrack is available on iOS (iPhone and iPad) and Android. Both parents and kids can use the app on whichever device they prefer, and everything stays in sync in real time.'
      },
    ]
  },
  {
    category: 'Features',
    items: [
      {
        q: 'How does the XP and levels system work?',
        a: 'Every completed chore or habit earns your child XP points. As XP accumulates, kids progress through five levels — from Seedling all the way to Champion. Each level unlocks recognition and can be tied to rewards you set. Parents control how much XP each task is worth, so you can make harder or more important tasks worth more.'
      },
      {
        q: 'What is a streak shield and how does it work?',
        a: 'A streak tracks how many consecutive days your child completes their tasks. Streak shields let kids protect that streak if life gets in the way — a sick day, a travel day, a rough week. Rather than losing their progress and getting discouraged, a shield absorbs the missed day and keeps the streak alive. Shields are earned through consistent performance, not given for free.'
      },
      {
        q: 'How does the sibling leaderboard work?',
        a: 'If you have multiple kids, the leaderboard shows their XP rankings side by side — updated as they complete tasks. It creates healthy, opt-in competition. Parents can toggle the leaderboard on or off per family.'
      },
      {
        q: 'How does the coin-to-dollar allowance system work?',
        a: 'Kids earn coins alongside XP for completing tasks. When they\'ve accumulated enough, they can request a reward redemption — which you as the parent approve. You set the coin-to-dollar exchange rate. Real money redemptions are parent-confirmed, so nothing moves without your tap.'
      },
      {
        q: 'Can I assign recurring chores or just one-time tasks?',
        a: 'Both. You can assign chores that repeat daily, on specific days of the week, or as one-off tasks. The app handles the scheduling automatically — kids wake up to a fresh task list every morning based on what you\'ve set up.'
      },
    ]
  },
  {
    category: 'Pricing',
    items: [
      {
        q: 'Is ThriveTrack free?',
        a: 'ThriveTrack is free to download and use. The core features — tasks, XP points, streaks, and rewards — are available on the free tier. Premium unlocks additional features including streak shields, the sibling leaderboard, advanced progress reports, and unlimited chore history.'
      },
      {
        q: 'What does the premium plan include?',
        a: 'Premium unlocks streak shields, the sibling leaderboard, the full XP level progression to Champion, weekly progress reports, unlimited task history, and priority support. It\'s $9.99/month for the whole family — all your kids under one subscription.'
      },
      {
        q: 'Is there a family plan or do I pay per child?',
        a: 'One subscription covers your entire household — one price, all your kids included. You\'re not charged per child. A family of five pays the same as a family of two.'
      },
      {
        q: 'Can I cancel anytime?',
        a: 'Yes. No contracts, no cancellation fees. If you cancel, you keep premium access through the end of your billing period, then drop to the free tier. Your data and your kids\' history stay intact.'
      },
    ]
  },
  {
    category: 'Families',
    items: [
      {
        q: 'Does ThriveTrack work for families with multiple kids?',
        a: 'Yes — and it\'s actually where ThriveTrack shines. Each child has their own profile, their own XP and level, and their own task list. The sibling leaderboard makes having multiple kids an advantage rather than a management headache.'
      },
      {
        q: 'Does ThriveTrack work for blended families or kids who split time between two homes?',
        a: 'Yes. ThriveTrack is built to work regardless of how many homes your child moves between. Each child\'s XP, streak, and progress travel with them — it\'s tied to the child\'s profile, not to a single household.'
      },
      {
        q: 'What if my child doesn\'t want to use it?',
        a: 'This is the most common concern we hear — and it\'s exactly why we built the gamification layer the way we did. XP, levels, streaks, and leaderboards aren\'t decoration; they\'re the proven mechanics that make behavior change stick. Most kids who resist chore charts engage with ThriveTrack once they realize there\'s actual progression involved.'
      },
      {
        q: 'Is ThriveTrack backed by research?',
        a: 'Yes. ThriveTrack is built on principles from behavioral science and child development research — specifically around habit formation, intrinsic motivation, and positive reinforcement. Kids with consistent routines show measurably better self-regulation, and ThriveTrack is designed to build exactly that kind of consistency.'
      },
    ]
  },
  {
    category: 'Privacy & Safety',
    items: [
      {
        q: 'Is my child\'s data safe?',
        a: 'Yes. Children\'s profiles are fully managed under the parent account. We do not sell your data or your child\'s data to third parties. ThriveTrack collects only what\'s needed to run the app — task history, XP progress, and reward logs.'
      },
      {
        q: 'Do kids need their own email address or account?',
        a: 'No. Child profiles exist under the parent account — kids never need an email address or separate login. Parents control the account entirely.'
      },
      {
        q: 'Does ThriveTrack show ads to my kids?',
        a: 'No. ThriveTrack is ad-free. The app is a subscription product — we\'re paid by families, not by advertisers. Your kids\' attention is not a product we sell.'
      },
    ]
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={() => setOpen(!open)}
      style={{
        background: '#fff',
        border: `1px solid ${open ? '#52B788' : '#E2EDE8'}`,
        borderRadius: 10,
        marginBottom: 8,
        cursor: 'pointer',
        boxShadow: open ? '0 2px 16px rgba(82,183,136,0.1)' : 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 22px', gap: 16,
      }}>
        <span style={{
          fontSize: 15, fontWeight: 600,
          color: open ? '#2D6A4F' : '#0F2D1E',
          lineHeight: 1.4, flex: 1,
        }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: '50%',
          background: open ? '#52B788' : '#F4F7F5',
          border: `1.5px solid ${open ? '#52B788' : '#E2EDE8'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#0F2D1E' : '#5A7A6A',
          fontSize: 18, fontWeight: 300, flexShrink: 0,
          transform: open ? 'rotate(45deg)' : 'none',
          transition: 'all 0.25s',
        }}>+</span>
      </div>
      {open && (
        <div style={{
          padding: '0 22px 20px',
          borderTop: '1px solid #E2EDE8',
          paddingTop: 16,
        }}>
          <p style={{
            fontSize: 14, color: '#5A7A6A',
            lineHeight: 1.75, margin: 0,
          }}>{a}</p>
        </div>
      )}
    </div>
  );
}

export default function Faq() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const categories = ['all', ...faqs.map(f => f.category)];
  const filtered = activeCategory === 'all'
    ? faqs
    : faqs.filter(f => f.category === activeCategory);

  return (
    <>
      <style>{RESPONSIVE_CSS}</style>
      <div style={{ fontFamily: ff.body, background: '#F4F7F5', minHeight: '100vh' }}>

        {/* ── NAV ─────────────────────────────────────────────────── */}
        <nav style={{
          position: 'sticky', top: 0, zIndex: 999,
          background: C.white,
          boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
          transition: 'box-shadow 0.25s',
          padding: '0 24px',
        }}>
          <div style={{
            maxWidth: 1100, margin: '0 auto', height: 64,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <a href="/" style={{ fontFamily: ff.head, fontSize: 24, color: C.dark, letterSpacing: 0.5, textDecoration: 'none' }}>
              🌱 ThriveTrack
            </a>

            {/* Desktop right */}
            <div className="mp-nav-desktop">
              <a href="/app" style={{
                fontFamily: ff.body, fontWeight: 700, fontSize: 14,
                color: C.primary, textDecoration: 'none',
              }}>Log In</a>
              <a
                href="https://apps.apple.com/app/thrivetrack/id6764627543"
                className="mp-amber-btn"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 14, padding: '10px 22px' }}
              >
                Download on App Store
              </a>
            </div>

            {/* Hamburger */}
            <button
              className="mp-nav-hamburger"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 22, color: C.dark, padding: 8, lineHeight: 1,
              }}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>

          {/* Mobile dropdown */}
          {menuOpen && (
            <div style={{
              background: C.white, borderTop: '1px solid #e8f4ed',
              padding: '16px 24px 20px',
            }}>
              <a href="/app" style={{
                display: 'block', padding: '10px 0',
                fontFamily: ff.body, fontWeight: 700, fontSize: 15,
                color: C.primary, textDecoration: 'none',
              }}>Log In</a>
              <a
                href="https://apps.apple.com/app/thrivetrack/id6764627543"
                className="mp-amber-btn"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                style={{
                  display: 'block', marginTop: 12, textAlign: 'center',
                  fontSize: 15, padding: '13px 20px',
                }}
              >
                Download on App Store
              </a>
            </div>
          )}
        </nav>

        {/* HERO BAND */}
        <div style={{
          background: C.dark, padding: '56px 48px 52px',
        }}>
          <div style={{ maxWidth: 780 }}>
            <p style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.14em', color: '#52B788', marginBottom: 12,
            }}>Help Center</p>
            <h1 style={{
              fontFamily: ff.head,
              fontSize: 'clamp(48px, 7vw, 80px)',
              color: '#fff', letterSpacing: 1, lineHeight: 0.95,
              marginBottom: 18,
            }}>Got <span style={{ color: '#F59E0B' }}>questions?</span><br />We've got answers.</h1>
            <p style={{
              fontSize: 16, color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.65, maxWidth: 540, margin: 0,
            }}>Everything you need to know about ThriveTrack — from getting started to understanding how XP levels and streak shields work.</p>
          </div>
        </div>

        {/* BODY */}
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '56px 48px 80px' }}>

          {/* CATEGORY FILTER */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 40 }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  fontSize: 12, fontWeight: 600,
                  padding: '7px 18px', borderRadius: 99,
                  border: `1.5px solid ${activeCategory === cat ? '#2D6A4F' : '#E2EDE8'}`,
                  background: activeCategory === cat ? '#2D6A4F' : '#fff',
                  color: activeCategory === cat ? '#fff' : '#5A7A6A',
                  cursor: 'pointer', fontFamily: 'inherit',
                  textTransform: 'capitalize',
                }}
              >{cat}</button>
            ))}
          </div>

          {/* FAQ SECTIONS */}
          {filtered.map(section => (
            <div key={section.category} style={{ marginBottom: 48 }}>
              <div style={{
                fontFamily: ff.head,
                fontSize: 13, letterSpacing: 2,
                color: '#52B788', textTransform: 'uppercase',
                marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10,
              }}>
                {section.category}
                <div style={{ flex: 1, height: 1, background: '#E2EDE8' }} />
              </div>
              {section.items.map((item, i) => (
                <FaqItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          ))}

          {/* STILL HAVE QUESTIONS */}
          <div style={{
            marginTop: 56, background: '#0F2D1E',
            borderRadius: 14, padding: '36px 40px',
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 24, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontFamily: ff.head,
                fontSize: 30, color: '#fff', marginBottom: 6,
              }}>Still have a question?</div>
              <div style={{ fontSize: 14, color: '#6B9E82', lineHeight: 1.55 }}>
                We're a founder-led team and we actually read every message.<br />
                Reach out and you'll hear back from a human.
              </div>
            </div>
            <a href="mailto:hello@thethrivetrack.com" style={{
              fontFamily: ff.head,
              fontSize: 16, letterSpacing: 1,
              padding: '12px 28px', borderRadius: 8,
              background: '#52B788', color: '#0F2D1E',
              textDecoration: 'none', flexShrink: 0,
            }}>✦ &nbsp;Contact Us</a>
          </div>
        </div>

        {/* ── FOOTER ──────────────────────────────────────────────── */}
        <footer style={{
          background: C.dark,
          borderTop: '1px solid rgba(82,183,136,0.12)',
          padding: '36px 24px 28px',
        }}>
          <div
            className="mp-footer-inner"
            style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', gap: 20 }}
          >
            <div>
              <a href="/" style={{ fontFamily: ff.head, fontSize: 20, color: C.white, marginBottom: 6, textDecoration: 'none', display: 'block' }}>
                🌱 ThriveTrack
              </a>
              <div style={{ fontFamily: ff.body, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
                Helping families grow together 🌱
              </div>
            </div>
            <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
              <a href="/privacy" className="mp-footer-link">Privacy Policy</a>
              <a href="/terms" className="mp-footer-link">Terms of Use</a>
              <a href="/faq" className="mp-footer-link">FAQ</a>
              <a href="mailto:hello@thethrivetrack.com" className="mp-footer-link">Support</a>
            </div>
          </div>
          <div style={{
            maxWidth: 1100, margin: '20px auto 0',
            borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 18,
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: ff.body, fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>
              © 2026 Thrive Track LLC. All rights reserved.
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
